import {
  FiStar,
  FiTruck,
  FiShield,
  FiRefreshCw,
  FiCheckCircle,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';

const QuickViewDetails = ({ book, rating, discount, inStock }) => {
  const { formatBookPrice, getBookAmount } = useCurrency();
  const subtitle = String(book.subtitle ?? '').trim();

  return (
    <div className="md:col-span-3 p-5 sm:p-7 flex flex-col">
      {(book.category?.name || (typeof book.category === 'string' && book.category)) && (
        <span className="text-[11px] font-semibold uppercase tracking-wider text-orange-600 mb-2">
          {book.category?.name || book.category}
        </span>
      )}

      <h2 className="text-xl sm:text-2xl font-bold text-black leading-snug mb-1.5 pr-10">
        {book.title}
      </h2>
      {subtitle && <p className="mb-3 text-sm leading-relaxed text-gray-700 break-words">{subtitle}</p>}

      {book.author && (
        <p className="text-sm text-black mb-3">
          by <span className="font-medium text-black">{book.author}</span>
        </p>
      )}

      {rating > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="flex items-center gap-1 bg-green-600 text-white text-xs font-semibold px-2 py-0.5 rounded">
            {rating.toFixed(1)} <FiStar className="fill-white" />
          </span>
          <span className="flex items-center gap-0.5">
            {[0, 1, 2, 3, 4].map((i) => (
              <FiStar
                key={i}
                className={`text-sm ${
                  i < Math.floor(rating)
                    ? 'text-amber-400 fill-amber-400'
                    : 'text-black'
                }`}
              />
            ))}
          </span>
          {(book.reviewCount || (Array.isArray(book.reviews) ? book.reviews.length : 0)) ? (
            <span className="text-xs text-black">
              ({book.reviewCount || book.reviews.length} reviews)
            </span>
          ) : null}
        </div>
      )}

      <div className="bg-gray-50 border border-gray-100 rounded-xl p-4 mb-4">
        <div className="flex items-end flex-wrap gap-x-3 gap-y-1">
          <span className="text-3xl font-extrabold text-black">
            {formatBookPrice(book)}
          </span>
          {getBookAmount(book, 'originalPrice') > getBookAmount(book) && (
              <span className="text-base text-black line-through mb-1">
                {formatBookPrice(book, 'originalPrice')}
              </span>
            )}
          {discount > 0 && (
            <span className="text-sm text-green-600 font-semibold mb-1">
              Save {discount}%
            </span>
          )}
        </div>
        {book.taxLabel ? (
          <p className="text-[11px] text-black mt-1">{book.taxLabel}</p>
        ) : book.taxRate != null ? (
          <p className="text-[11px] text-black mt-1">
            {book.taxInclusive ? 'Tax included' : 'Tax calculated at checkout'} ({Number(book.taxRate)}%)
          </p>
        ) : null}
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {inStock ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
            <FiCheckCircle /> In Stock
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">
            Coming Soon
          </span>
        )}
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full">
            <FiTruck /> Delivery
          </span>
      </div>

      {book.description && (
        <p className="text-sm text-black leading-relaxed mb-4 line-clamp-4">
          {book.description}
        </p>
      )}

      <div className="grid grid-cols-3 gap-2 mt-auto">
        <Link
          to="/shipping"
          className="flex flex-col items-center text-center gap-1 p-2 rounded-lg bg-gray-50 transition hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-400"
          aria-label="View delivery information"
        >
          <FiTruck className="text-orange-600 text-base" />
          <span className="text-[10px] text-black font-medium leading-tight">
            Delivery
          </span>
        </Link>
        <Link
          to="/return"
          className="flex flex-col items-center text-center gap-1 p-2 rounded-lg bg-gray-50 transition hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-400"
          aria-label="View returns information"
        >
          <FiRefreshCw className="text-orange-600 text-base" />
          <span className="text-[10px] text-black font-medium leading-tight">
            Easy Returns
          </span>
        </Link>
        <Link
          to="/secure-shopping"
          className="flex flex-col items-center text-center gap-1 p-2 rounded-lg bg-gray-50 transition hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-400"
          aria-label="View secure payment information"
        >
          <FiShield className="text-orange-600 text-base" />
          <span className="text-[10px] text-black font-medium leading-tight">
            Secure Payment
          </span>
        </Link>
      </div>
    </div>
  );
};

export default QuickViewDetails;
